import React, { useState } from 'react';
import { Button, Tooltip } from "@mui/material";
import FormEditModal from './common/FormEditModal';
import './../css/AddEditButton.css';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
    AROMA,
    CANNABIS_COMPOUNDS,
    COMPOUND,
    MARKET_CBD,
    MUTAGENICITY,
    PHARMACO_DYNAMICS,
    PHYSICAL_CHARACTERIZATION_CBD,
    REFERENCES,
    save as saveCompounds,
    STABILITY,
    TOXICANTS_DISEASE
} from './../api/Compounds';
import { save as saveStrains, STRAINS } from './../api/Strains';
import {
    CBD_PK_INDICES,
    save as saveStudies,
    SC_PK,
    SC_THERAPEUTIC_INDICATIONS,
    STUDIES_METABOLITES
} from './../api/Studies';
import {
    save as saveProducts,
    PRODUCT_DEGRADATION,
    PRODUCT_DEGRADATION_COMPOUNDS,
    PRODUCT_ANALYSIS_VALUES,
    PRODUCTS_ANALYSIS_METHODS,
    PRODUCTS_ANALYSIS_REPORTS
} from './../api/Products';
import { useMessage } from "./ContextMessage";
import { isNotEmpty } from "../utils/string";
import Events from "../events";
import { EventsType } from "../events-types";

// Interface for props
interface AddNewDataProps {
    listsOfObjects: { [key: string]: any };
}

const AddNewData: React.FC<AddNewDataProps> = ({ listsOfObjects }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedDataType, setSelectedDataType] = useState('');
    const [dataToSave, setDataToSave] = useState({});
    const { showWarning } = useMessage();

    const toggleMenu = () => {
        setSelectedDataType('');
        setMenuOpen(!menuOpen);
    };

    const handleOptionClick = (dataType: string) => {
        setSelectedDataType(dataType);
        setMenuOpen(false);
    };

    const saveData = async (data: any) => {
        try {
            let res;

            switch (selectedDataType) {
                case COMPOUND:
                case CANNABIS_COMPOUNDS:
                case AROMA:
                case REFERENCES:
                case MARKET_CBD:
                case PHYSICAL_CHARACTERIZATION_CBD:
                case STABILITY:
                case MUTAGENICITY:
                case TOXICANTS_DISEASE:
                case PHARMACO_DYNAMICS:
                    res = await saveCompounds(selectedDataType, data);
                    break;
                case STRAINS:
                    res = await saveStrains(selectedDataType, data);
                    break;
                case SC_PK:
                case SC_THERAPEUTIC_INDICATIONS:
                case STUDIES_METABOLITES:
                case CBD_PK_INDICES:
                    res = await saveStudies(selectedDataType, data);
                    break;
                case PRODUCT_ANALYSIS_VALUES:
                case PRODUCTS_ANALYSIS_REPORTS:
                case PRODUCTS_ANALYSIS_METHODS:
                case PRODUCT_DEGRADATION:
                case PRODUCT_DEGRADATION_COMPOUNDS:
                    res = await saveProducts(selectedDataType, data);
                    break;
                default:
                    throw new Error('Invalid DATATYPE');
            }
            setDataToSave(res);
            setMenuOpen(false);
            Events.trigger(EventsType.RELOAD, {});
        } catch (error) {
            showWarning('Compound value was NOT saved!');
        }
    };

    // Map data types to user-friendly labels
    const dataTypeLabels: { [key: string]: string } = {
        [AROMA]: 'Aroma',
        [CANNABIS_COMPOUNDS]: 'Cannabis Compound',
        [COMPOUND]: 'Compound',
        [REFERENCES]: 'Reference',
        [STRAINS]: 'Strains',
        [SC_THERAPEUTIC_INDICATIONS]: 'Therapeutic Indication',
        [MARKET_CBD]: 'CBD Market',
        [PHYSICAL_CHARACTERIZATION_CBD]: 'Physical Characterization',
        [STUDIES_METABOLITES]: 'Metabolite',
        [SC_PK]: 'PK',
        [CBD_PK_INDICES]: 'PK Indices',
        [PHARMACO_DYNAMICS]: 'Pharmaco Dynamics',
        [PRODUCTS_ANALYSIS_METHODS]: 'Product Analysis Methods',
        [PRODUCT_ANALYSIS_VALUES]: 'Product Analysis Values',
        [PRODUCTS_ANALYSIS_REPORTS]: 'Product Analysis Report',
        [PRODUCT_DEGRADATION]: 'Product Degradation',
        [PRODUCT_DEGRADATION_COMPOUNDS]: 'Product Degradation Compound',
        [MUTAGENICITY]: 'Mutagenicity',
        [STABILITY]: 'Stability',
        [TOXICANTS_DISEASE]: 'Toxicants Disease'
    };

    // Sort data types by their labels
    const sortedDataTypes = Object.keys(listsOfObjects).sort((a, b) => {
        const labelA = (dataTypeLabels[a] || a).toLowerCase();
        const labelB = (dataTypeLabels[b] || b).toLowerCase();
        return labelA.localeCompare(labelB);
    });

    return (
        <div className="add-edit-button-container">
            <Tooltip title="Create/Add new data" placement="top" arrow>
                <Button
                    className="add-edit-button"
                    style={{ height: '32px', boxShadow: '0px 0px 0px 0px', marginRight: '5px' }}
                    onClick={toggleMenu}
                    variant="contained"
                    color="primary"
                    aria-label="Add new DATA"
                >
                    <AddCircleOutlineIcon />
                </Button>
            </Tooltip>

            {menuOpen && (
                <div className="menu-options">
                    {sortedDataTypes.map(dataType => (
                        <div
                            key={dataType}
                            className="menu-option"
                            onClick={() => handleOptionClick(dataType)}
                        >
                            {dataTypeLabels[dataType] || dataType}
                        </div>
                    ))}
                </div>
            )}

            {isNotEmpty(selectedDataType) && (
                <FormEditModal
                    defaultOpen={true}
                    save={saveData}
                    data={dataToSave}
                    schema={listsOfObjects[selectedDataType] || {}}
                />
            )}
        </div>
    );
};

export default AddNewData;
